import { Button, Link, Message, TextInput, Typography } from "@fullscript/aviary-web";
import type { SyntheticEvent } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { EmailInput } from "@shared/components/EmailInput/EmailInput";
import { gRecaptchaExecute } from "@shared/utils/gRecaptchaExecute/gRecaptchaExecute";
import { RecaptchaErrorBox } from "@unauthenticated/shared/components/RecaptchaErrorBox/RecaptchaErrorBox";
import { useGlobalConfig } from "@unauthenticated/shared/context/GlobalConfig/GlobalConfig";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import { useFsConnectJoinStoreUnauthenticatedSignUpSignInMutation } from "./FsConnectJoinStore.mutation.generated";
import { areRequiredFieldsValid, isNameInvalid } from "./helpers";

import * as styles from "./PatientConnectSignUpForm.styles";

const MAGIC_LINK_OUTCOME = "magicLink";

interface Props {
  signInHref: string;
  storeId?: string;
  requestedPractitionerId?: string;
  onMagicLinkSent: (email: string) => void;
}

const PatientConnectSignUpForm = ({
  signInHref,
  storeId,
  requestedPractitionerId,
  onMagicLinkSent,
}: Props) => {
  const { t } = useTranslation();
  const { emailRegex } = useGlobalConfig();

  const [firstName, setFirstName] = useState("");
  const [isFirstNameDirty, setIsFirstNameDirty] = useState(false);
  const [lastName, setLastName] = useState("");
  const [isLastNameDirty, setIsLastNameDirty] = useState(false);
  const [email, setEmail] = useState("");
  const [isEmailDirty, setIsEmailDirty] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [recaptchaError, setRecaptchaError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const failSubmit = () => {
    setIsSubmitting(false);
    setSubmitError(t(l.connect.form.submitError));
  };

  const [joinStore, { data, reset }] = useFsConnectJoinStoreUnauthenticatedSignUpSignInMutation({
    onCompleted: payload => {
      const result = payload.auth?.fsConnectJoinStore;

      if (!result) {
        failSubmit();

        return;
      }

      if (result.errors.message) {
        setIsSubmitting(false);

        return;
      }

      if (result.outcome === MAGIC_LINK_OUTCOME) {
        onMagicLinkSent(email);

        return;
      }

      window.location.assign(result.redirectPath ?? authRoutes.base);
    },
    onError: failSubmit,
  });

  const errorMessage = data?.auth?.fsConnectJoinStore?.errors.message ?? submitError;

  const joinTarget =
    storeId && requestedPractitionerId ? { storeId, requestedPractitionerId } : null;

  const canSubmit =
    areRequiredFieldsValid({ firstName, lastName, email }, emailRegex) &&
    !!joinTarget &&
    !isSubmitting;

  // The DS5 field renders its own "Email is required" caption, so the shared wrapper is
  // only asked to validate once there is a value — otherwise both emit that same message.
  const shouldValidateEmail = isEmailDirty && !!email;

  const dismissError = () => {
    setSubmitError(null);
    reset();
  };

  const handleRecaptchaError = (hasError: boolean) => {
    setIsSubmitting(false);
    setRecaptchaError(hasError);
  };

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitError(null);
    setRecaptchaError(false);
    setIsSubmitting(true);

    gRecaptchaExecute(gToken => {
      joinStore({
        variables: {
          input: { firstName, lastName, email, ...joinTarget, captchaToken: gToken },
        },
      });
    }, handleRecaptchaError);
  };

  return (
    <div css={styles.card}>
      <form onSubmit={handleSubmit} css={styles.form} noValidate data-e2e="connect-sign-up-form">
        {!!recaptchaError && <RecaptchaErrorBox />}
        {!!errorMessage && (
          <Message intention="critical" label={errorMessage} onDismiss={dismissError} />
        )}
        <TextInput
          id="firstName"
          name="firstName"
          label={t(l.common.FirstName)}
          value={firstName}
          onChange={event => setFirstName(event.target.value)}
          onBlur={() => setIsFirstNameDirty(true)}
          errorCaption={
            isNameInvalid(firstName, isFirstNameDirty) ? t(l.common.FirstNameInvalid) : undefined
          }
          required
        />
        <TextInput
          id="lastName"
          name="lastName"
          label={t(l.common.LastName)}
          value={lastName}
          onChange={event => setLastName(event.target.value)}
          onBlur={() => setIsLastNameDirty(true)}
          errorCaption={
            isNameInvalid(lastName, isLastNameDirty) ? t(l.common.LastNameInvalid) : undefined
          }
          required
        />
        <EmailInput
          designSystemVersion="ds5"
          value={email}
          setEmail={setEmail}
          onChange={event => setEmail(event.target.value)}
          onBlur={() => setIsEmailDirty(true)}
          isDirty={shouldValidateEmail}
          hideSuggestion
          required
        />
        <div css={styles.submitRow}>
          <Button
            type="submit"
            size="large"
            isFullWidth
            isPending={isSubmitting}
            disabled={!canSubmit}
          >
            {t(l.common.CreateAccount)}
          </Button>
        </div>
        <div css={styles.signInPrompt}>
          <Typography variant="footnote" isMarginless>
            {t(l.connect.form.alreadyHaveAnAccount)}
          </Typography>
          <Link href={signInHref} hideUnderline>
            <Typography as="span" variant="footnote" fontWeight="bold" isMarginless>
              {t(l.common.SignIn)}
            </Typography>
          </Link>
        </div>
      </form>
    </div>
  );
};

export { PatientConnectSignUpForm };
export type { Props as PatientConnectSignUpFormProps };
